/**
 * Theme preference handling: light / dark, default light. The preference lives in
 * a cookie scoped to the parent domain (.kiss.cat) so the choice follows the user
 * across every kiss.cat app (auth, tasks, ...). SSR reads the cookie to set
 * data-theme on <html> in the initial markup, so there is no flash of the wrong
 * theme; a localStorage mirror is a same-origin fast path. On non-kiss.cat hosts
 * (localhost, deploy previews) the parent-domain cookie would be rejected, so the
 * cookie is written host-only there and dev still works.
 */
export type ThemePreference = 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_COOKIE = 'kc_theme';
export const THEME_STORAGE_KEY = 'kc-theme';
export const DEFAULT_THEME: ResolvedTheme = 'light';

// The parent domain the cookie is scoped to so every kiss.cat subdomain shares it.
const SHARED_DOMAIN = 'kiss.cat';
// A year, matching the previous cookie lifetime.
const COOKIE_MAX_AGE = 31536000;

/** Normalise any stored/queried value to a valid theme (legacy 'system' → light). */
export function normaliseTheme(value: string | null | undefined): ResolvedTheme {
	return value === 'dark' ? 'dark' : 'light';
}

/**
 * The cookie `domain` for a host: `.kiss.cat` on the real site (shared across
 * subdomains), or null on localhost / preview hosts where a parent-domain cookie
 * would be rejected — there the cookie stays host-only.
 */
export function themeCookieDomain(hostname: string): string | null {
	if (hostname === SHARED_DOMAIN || hostname.endsWith(`.${SHARED_DOMAIN}`)) {
		return `.${SHARED_DOMAIN}`;
	}
	return null;
}

/** Build the string the toggle writes via document.cookie. */
export function buildThemeCookie(
	preference: ThemePreference,
	hostname: string,
	secure: boolean,
): string {
	const parts = [
		`${THEME_COOKIE}=${encodeURIComponent(preference)}`,
		'path=/',
		`max-age=${COOKIE_MAX_AGE}`,
		'samesite=lax',
	];
	const domain = themeCookieDomain(hostname);
	if (domain) parts.push(`domain=${domain}`);
	if (secure) parts.push('secure');
	return parts.join('; ');
}

/** Read the theme preference out of a raw Cookie header — null when absent. */
export function readThemeCookie(cookieHeader: string | null | undefined): ResolvedTheme | null {
	if (!cookieHeader) return null;
	const match = cookieHeader.match(new RegExp(`(?:^|; )${THEME_COOKIE}=([^;]+)`));
	const value = match?.[1];
	if (value == null) return null;
	return normaliseTheme(decodeURIComponent(value));
}

/** The theme SSR should render for a request: the cookie value, else the default. */
export function resolveInitialTheme(cookieHeader: string | null | undefined): ResolvedTheme {
	return readThemeCookie(cookieHeader) ?? DEFAULT_THEME;
}

/**
 * Inline <head> script applying the theme before first paint, as a fallback for
 * documents SSR could not resolve (e.g. a client-only app) and to pick up a cookie
 * set on another subdomain. The cookie wins; an SSR-set data-theme is respected
 * when there is no cookie; the localStorage mirror is the last resort. Injected via
 * dangerouslySetInnerHTML. A legacy 'system' value (from before the option was
 * removed) falls back to light.
 */
export const prePaintScript = `(function(){try{var d=document.documentElement;var m=document.cookie.match(/(?:^|; )${THEME_COOKIE}=([^;]+)/);if(m){d.dataset.theme=decodeURIComponent(m[1])==='dark'?'dark':'light';return;}if(d.dataset.theme){return;}var s=null;try{s=localStorage.getItem('${THEME_STORAGE_KEY}');}catch(e){}d.dataset.theme=s==='dark'?'dark':'light';}catch(e){document.documentElement.dataset.theme='light';}})();`;

/** Persist a preference and apply it immediately (client only). */
export function applyThemePreference(preference: ThemePreference): void {
	document.documentElement.dataset.theme = preference;

	// localStorage can throw in privacy mode — the cookie still carries the value.
	try {
		localStorage.setItem(THEME_STORAGE_KEY, preference);
	} catch {
		/* storage unavailable */
	}

	const secure = window.location.protocol === 'https:';
	document.cookie = buildThemeCookie(preference, window.location.hostname, secure);
}

/**
 * The preference the toggle should show on the client: the shared cookie first
 * (so a choice made on another subdomain is reflected), then the localStorage
 * mirror, else the default.
 */
export function readStoredPreference(): ThemePreference {
	if (typeof document === 'undefined') return DEFAULT_THEME;
	const fromCookie = readThemeCookie(document.cookie);
	if (fromCookie) return fromCookie;
	try {
		return normaliseTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
	} catch {
		return DEFAULT_THEME;
	}
}
